import { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import Cookies from "js-cookie";
import { FetchAllApps } from "api/apps";
import NewFeaturesModal from "./FeatureModal";
import { FetchAllResources } from "api/resources";
import { navigateToDomain } from "utilities/index";

const BLOCK_KEY_PREFIX = "feature_block_until_";

export default function FeaturePopup() {
  const cookieStartupId = Cookies.get("X-ST-ID");
  const navigate = useNavigate();
  const { data: resources, isLoading: isLoadingResources } =
    FetchAllResources(cookieStartupId);
  const { data: features, isLoading } = FetchAllApps(cookieStartupId);
  const [params, setParams] = useSearchParams();
  const [selectedResource, setSelectedResource] = useState(null);
  const utm_resource = params.get("utm_resource");
  const utm_campaign = params.get("utm_campaign");
  const startupResourceContextRaw = localStorage.getItem(
    "startup_resource_utm_context"
  );
  const [open, setOpen] = useState(false);

  const BLOCK_KEY = cookieStartupId
    ? `${BLOCK_KEY_PREFIX}${cookieStartupId}`
    : null;

  // --- helpers: block until tomorrow ---

  const setBlockUntilTomorrow = () => {
    if (!cookieStartupId) return;

    const now = new Date();
    const tomorrow = new Date(
      now.getFullYear(),
      now.getMonth(),
      now.getDate() + 1,
      0,
      0,
      0,
      0
    );

    localStorage.setItem(BLOCK_KEY, String(tomorrow.getTime()));
    Cookies.set(BLOCK_KEY, "1", {
      expires: tomorrow,
      path: "/",
    });
  };

  const isBlocked = () => {
    if (!cookieStartupId) return false;
    const ts = Number(localStorage.getItem(BLOCK_KEY) || 0);
    return ts && Date.now() < ts;
  };

  // --- get features to show (stable callback) ---

  const getFeaturesToShow = useCallback((featuresArray) => {
    return (
      featuresArray?.filter((f) => {
        const baseKey = `feature_seen_${cookieStartupId}_${f.id}`;
        const seenCount = Number(
          localStorage.getItem(`${baseKey}_count`) || 0
        );
        const lastSeen = Number(
          localStorage.getItem(`${baseKey}_lastSeen`) || 0
        );
        const now = Date.now();
        const minDuration = f.min_duration || 24 * 60 * 60 * 1000;
        const durationOk = !lastSeen || now - lastSeen >= minDuration;
        const hasNoReport = !f.report_available || !f.report_url;
        return (
          f.enabled &&
          f.status === false &&
          seenCount < (f.max_views || 3) &&
          durationOk &&
          hasNoReport
        );
      }) || []
    );
  }, [cookieStartupId]);

  // ✅ STABLE: only recompute when features or cookieStartupId change
  const featuresToShow = useMemo(
    () => getFeaturesToShow(features),
    [features, getFeaturesToShow]
  );

  // --- decide when to open resource / feature popup ---

  useEffect(() => {
    if (!cookieStartupId || isBlocked()) return;

    let foundResource = false;

    // 1. Try to show resource first
    if (
      !isLoadingResources &&
      Array.isArray(resources) &&
      resources.length > 0 &&
      (utm_resource || utm_campaign || startupResourceContextRaw)
    ) {
      let selectedRes = null;

      if (utm_resource) {
        selectedRes = resources.find(
          (r) => r.id === utm_resource || r.slug === utm_resource
        );
      } else if (utm_campaign) {
        selectedRes = resources.find(
          (r) => r.utm_campaign === utm_campaign
        );
      }

      if (!selectedRes && startupResourceContextRaw) {
        try {
          const parsed = JSON.parse(startupResourceContextRaw);
          selectedRes = resources.find(
            (r) =>
              r.id === parsed.id ||
              r.slug === parsed.slug ||
              r.utm_campaign === parsed.utm_campaign
          );
        } catch (e) {}
      }

      if (selectedRes) {
        setSelectedResource(selectedRes);
        setOpen(true);
        foundResource = true;
      }
    }

    // 2. If no resource shown, show features
    // if (!foundResource) {
    //   if (!isLoading && Array.isArray(features) && featuresToShow.length > 0) {
    //     setOpen(true);
    //     if (params.get("new-features") !== "true") {
    //       params.set("new-features", "true");
    //       setParams(params, { replace: true });
    //     }
    //   }
    // }
  }, [
    isLoading,
    features,
    cookieStartupId,
    params,
    setParams,
    isLoadingResources,
    resources,
    utm_resource,
    utm_campaign,
    startupResourceContextRaw,
    featuresToShow.length, // ✅ stable dependency
  ]);

  // --- track shown features (only once when modal opens) ---

  useEffect(() => {
    if (!cookieStartupId || isBlocked() || !open) return;

    // skip if resource modal is showing
    if (selectedResource || (utm_resource || utm_campaign || startupResourceContextRaw)) {
      return;
    }

    // track features only once
    const shownFeatures = getFeaturesToShow(features);
    if (shownFeatures.length > 0) {
      shownFeatures.forEach((f) => {
        const baseKey = `feature_seen_${cookieStartupId}_${f.id}`;
        const seenCount = Number(
          localStorage.getItem(`${baseKey}_count`) || 0
        );
        localStorage.setItem(`${baseKey}_count`, String(seenCount + 1));
        localStorage.setItem(`${baseKey}_lastSeen`, String(Date.now()));
      });
    }
  }, [open]); // ✅ only depends on `open` - runs once when modal opens

  // --- close handler: block for the day ---

  const handleClose = () => {
    if (selectedResource) {
    //   setBlockUntilTomorrow();
      navigate("/");
      setTimeout( () => {
          localStorage.removeItem("startup_resource_utm_context");
      }, 100) 
    } else if (params.get("new-features")) {
      setBlockUntilTomorrow();
      params.delete("new-features");
      setParams(params, { replace: true });
    }
    setOpen(false); // always close at end
    setSelectedResource(null);
  };

  const handleViewResource = (resource) => {
    return navigateToDomain("main", "resources/" + resource?.slug);
  };

  return (
    <NewFeaturesModal
      open={open}
      onClose={handleClose}
      features={
        selectedResource
          ? [
              {
                ...selectedResource,
                image: selectedResource?.thumbnail_image,
                button_text: "View Resource",
                default_link: handleViewResource(selectedResource),
              },
            ]
          : featuresToShow // ✅ stable!
      }
    />
  );
}
